import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Act, Article, FilterState, Recital, Annex, RecitalGroup, CitedNorm } from '@/types';
import instrumentsData from '@/data/instruments/index.json';
import citedNormsData from '@/data/cited_norms.json';
import { printLinkCss, PRINT_LINK_HTML } from '@/utils/printLink';

const CITED_NORMS = citedNormsData as Record<string, CitedNorm>;

const ACT_SHORT_NAMES: Record<string, string> = {
  amld:  'AML Directive',
  amlr:  'AML Substantive Regulation',
  amlar: 'AMLA Founding Regulation',
  tfr:   'Funds Transfer Regulation',
};


const ACT_COLORS: Record<string, string> = {
  amld:  '#1D4ED8',
  amlr:  '#0D9488',
  amlar: '#7C3AED',
  tfr:   '#C2410C',
};
const INSTRUMENTS = instrumentsData as Record<string, {status: string; type: string; title?: string; legalBasis?: string}>;
import { ArticleBody } from './ArticleBody';
import { useExpandedArticles } from '@/hooks/useExpandedArticles';
import { ACT_CARDS } from '@/data/actCards';
import styles from './ActColumn.module.css';

interface SectionItem {
  chapterTitle: string;
  sectionTitle: string | null;
  articles: Article[];
}

interface Props {
  act: Act;
  filters: FilterState;
  onInstrumentClick?: (instrumentId: string) => void;
  collapseSignal?: number;
  onCrossRefClick?: (actId: string, num: number | string) => void;
  /** Reports how many articles pass the current filters (for the search-bar counter). */
  onVisibleCount?: (actId: string, n: number) => void;
  crossRefTarget?: {actId: string; num: number | string; ts: number} | null;
  /** Hide the built-in EUR-Lex ELI header link (used when the column shows an
   *  un-adopted mandate whose EUR-Lex link is presented elsewhere). Default false. */
  hideSourceLink?: boolean;
  /** Hide the per-article EUR-Lex EN/IT source row (mandate context). Default false. */
  articleHideSources?: boolean;
  /** Override the per-article "Read full text" target (mandate context). */
  articleReaderHref?: (num: number | string) => string;
}

function articleMatchesFilter(article: Article, filters: FilterState, instruments: Record<string, {status: string; type: string; title?: string; legalBasis?: string}>, searchIndex?: Map<number | string, string>): boolean {
  const { query, hasMandates } = filters;
  if (hasMandates && (!article.rtsIts || article.rtsIts.length === 0)) return false;
  if (filters.types.length > 0) {
    const hasType = article.rtsIts?.some(id => {
      const inst = instruments[id]; const t = inst ? inst.type : undefined;
      return t && filters.types.includes(t as 'RTS' | 'ITS' | 'GL');
    });
    if (!hasType) return false;
  }
  if (filters.statuses.length > 0) {
    const hasStatus = article.rtsIts?.some(id => {
      const s = instruments[id]?.status;
      return s && filters.statuses.includes(s as 'adopted' | 'final_report' | 'consultation_closed' | 'consultation_open' | 'planned');
    });
    if (!hasStatus) return false;
  }
  if (query.trim()) {
    const q = query.toLowerCase();
    // Use pre-computed search index when available (avoids O(n*m) per keystroke)
    const indexedText = searchIndex?.get(article.num);
    if (indexedText !== undefined) {
      if (!indexedText.includes(q)) return false;
    } else {
      const inTitle   = article.title.toLowerCase().includes(q);
      const inNum     = String(article.num).includes(q);
      const inSummary = article.summary?.toLowerCase().includes(q) ?? false;
      const inText    = article.textEN?.paragraphs?.some(
        p => p.text.toLowerCase().includes(q) ||
             p.points?.some(pt => pt.toLowerCase().includes(q))
      ) ?? false;
      const inMandate = article.rtsIts?.some(id => {
        const inst = instruments[id] as {title?: string; legalBasis?: string} | undefined;
        return inst?.title?.toLowerCase().includes(q) || inst?.legalBasis?.toLowerCase().includes(q);
      }) ?? false;
      if (!inTitle && !inNum && !inSummary && !inText && !inMandate) return false;
    }
  }
  return true;
}

export function ActColumn({ act, filters, onInstrumentClick, collapseSignal, onCrossRefClick, crossRefTarget, hideSourceLink = false, articleHideSources = false, articleReaderHref, onVisibleCount }: Props) {
  const { toggle, isExpanded, collapseAll, expandAll, open } = useExpandedArticles();

  const getArticleKey = useCallback(
    (num: number | string) => `${act.id}-${num}`,
    [act.id]
  );

  // Open article when cross-ref target changes
  useEffect(() => {
    if (crossRefTarget) {
      open(getArticleKey(crossRefTarget.num));
    }
  }, [crossRefTarget, getArticleKey, open]);

  // Collapse all when signal changes
  useEffect(() => {
    if (collapseSignal && collapseSignal > 0) { collapseAll(); setRecitalsOpen(false); setAnnexesOpen(false); }
  }, [collapseSignal, collapseAll]);

  const [recitalsOpen, setRecitalsOpen] = useState(false);
  const [annexesOpen, setAnnexesOpen] = useState(false);
  const [showCard, setShowCard] = useState(false);

  // Pre-compute flat article list and search index once per act (not per keystroke)
  const allSections: SectionItem[] = useMemo(() => {
    const sections: SectionItem[] = [];
    for (const ch of act.chapters) {
      if (ch.sections && ch.sections.length > 0) {
        for (const sec of ch.sections) {
          sections.push({ chapterTitle: ch.title, sectionTitle: sec.title, articles: sec.articles });
        }
      } else {
        sections.push({ chapterTitle: ch.title, sectionTitle: null, articles: ch.articles ?? [] });
      }
    }
    return sections;
  }, [act]);

  // Pre-compute search text per article (concatenated, lowercased) — expensive to compute per keystroke
  const articleSearchIndex = useMemo(() => {
    const idx = new Map<number | string, string>();
    for (const sec of allSections) {
      for (const a of sec.articles) {
        const text = [
          a.title,
          String(a.num),
          a.summary ?? '',
          ...(a.textEN?.paragraphs?.flatMap(p => [p.text, ...(p.points ?? [])]) ?? []),
          ...(a.rtsIts?.flatMap(id => {
            const inst = INSTRUMENTS[id] as {title?: string; legalBasis?: string} | undefined;
            return [inst?.title ?? '', inst?.legalBasis ?? ''];
          }) ?? []),
        ].join(' ').toLowerCase();
        idx.set(a.num, text);
      }
    }
    return idx;
  }, [allSections]);
  useEffect(() => {
    if (!onVisibleCount) return;
    const n = allSections.reduce((s, sec) => s + sec.articles.filter(a => articleMatchesFilter(a, filters, INSTRUMENTS, articleSearchIndex)).length, 0);
    onVisibleCount(act.id, n);
  }, [filters, allSections, articleSearchIndex, act.id, onVisibleCount]);

  const actColor = `var(--${act.id})`;

  return (
    <div className={styles.column} style={{ '--act-color': actColor } as React.CSSProperties}>
      {showCard && ACT_CARDS[act.id] && (
        <div className={styles.tfrOverlay} onClick={() => setShowCard(false)}>
          <div className={styles.tfrModal} onClick={e => e.stopPropagation()}>
            <div className={styles.tfrModalHeader} style={{ background: actColor }}>
              <h3>{ACT_CARDS[act.id].title}</h3>
              <button className={styles.tfrModalClose} onClick={() => setShowCard(false)} aria-label="Close">✕</button>
            </div>
            <div className={styles.tfrModalBody}>
              {ACT_CARDS[act.id].body.map((b, i) => (
                b.kind === 'note'
                  ? <p key={i} className={styles.tfrModalNote} dangerouslySetInnerHTML={{ __html: b.html }} />
                  : <p key={i} dangerouslySetInnerHTML={{ __html: b.html }} />
              ))}
              {ACT_CARDS[act.id].list && (
                <ul>
                  {ACT_CARDS[act.id].list!.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: li }} />)}
                </ul>
              )}
              {ACT_CARDS[act.id].listAfter?.map((b, i) => (
                b.kind === 'note'
                  ? <p key={i} className={styles.tfrModalNote} dangerouslySetInnerHTML={{ __html: b.html }} />
                  : <p key={i} dangerouslySetInnerHTML={{ __html: b.html }} />
              ))}
              <div className={styles.tfrModalSources}>
                <strong>Official sources</strong>
                {ACT_CARDS[act.id].sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <button
            className={styles.badge}
            data-tour="act-badge"
            style={{ background: actColor }}
            onClick={() => setShowCard(true)}
            title={`About the ${act.shortName} — part of the EU AML/CFT package`}
            aria-label={`About the ${act.shortName}`}
          >
            {act.shortName}
          </button>
          <div className={styles.headerControls} data-tour="col-tools">
            <button
              className={styles.colCtrlBtn}
              title="Expand all articles"
              onClick={() => {
                const keys = allSections.flatMap(s => s.articles.map(a => getArticleKey(a.num)));
                expandAll(keys);
              }}
            >⊞</button>
            <button
              className={styles.colCtrlBtn}
              title="Collapse all articles"
              onClick={() => { collapseAll(); setRecitalsOpen(false); setAnnexesOpen(false); }}
            >⊟</button>
            <a
              href={`https://eur-lex.europa.eu/eli/${act.type}/${act.year}/${act.num}/oj/eng`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.eurLexLink}
              style={hideSourceLink ? { display: 'none' } : undefined}
            >
              EUR-Lex ↗
            </a>
          </div>
        </div>
        <div className={styles.fullName}>{act.fullName}</div>
        <div className={styles.celex}>{act.celex} · {ACT_SHORT_NAMES[act.id] ?? ''}</div>
      </div>

      {act.recitals && act.recitals.length > 0 && (
        <div className={styles.collapsibleSection}>
          <button
            className={`${styles.sectionToggle} ${styles.sectionToggleRecitals}`}
            data-tour="recitals"
            onClick={() => setRecitalsOpen(o => !o)}
          >
            <span>📜 Recitals ({act.recitals.length})</span>
            <span>{recitalsOpen ? '▼' : '▶'}</span>
          </button>
          {recitalsOpen && (
            <div className={styles.recitalList}>
              {act.recitalGroups && act.recitalGroups.length > 0 ? (
                (act.recitalGroups as RecitalGroup[]).map(g => (
                  <div key={g.id} className={styles.recitalGroupCard}>
                    <div className={styles.recitalGroupHeader}>
                      <span className={styles.recitalGroupTitle}>{g.title}</span>
                      <span className={styles.recitalGroupCount}>{g.recitalNums.length} recitals</span>
                    </div>
                    {g.superSummary && <p className={styles.recitalGroupSummary}>{g.superSummary}</p>}
                    <div className={styles.recitalGroupFooter}>
                      <span className={styles.recitalGroupNums}>({g.recitalNums[0]}–{g.recitalNums[g.recitalNums.length-1]})</span>
                      <button className={styles.recitalReaderBtn} onClick={() => openRecitalGroupReader(g, act, act.id)}>📖 Read recitals ↗</button>
                    </div>
                    {g.summary_disclaimer && <span className={styles.recitalGroupDisclaimer}>{g.summary_disclaimer}</span>}
                  </div>
                ))
              ) : (
              <>{act.recitals.map((r: Recital) => {
                const rKey = 'rct_' + r.num;
                const rExpanded = isExpanded(rKey);
                const fnotes = (r.footnoteRefs || []).map((n: number) => act.footnotes?.find((f: {num:number;text:string}) => f.num === n)).filter(Boolean);
                return (
                  <div key={r.num} className={styles.recitalItem}>
                    <button className={styles.recitalHeader} onClick={() => toggle(rKey)}>
                      <span className={styles.recitalNum}>({r.num})</span>
                      <span className={styles.recitalPreview}>
                        {rExpanded ? '' : stripHtml(r.text).slice(0, 120) + (r.text.length > 120 ? '…' : '')}
                      </span>
                      <span className={styles.recitalChevron}>{rExpanded ? '▼' : '▶'}</span>
                    </button>
                    {rExpanded && (
                      <div className={styles.recitalBody}>
                        {'verified' in r && (
                          <span
                            className={r.verified ? styles.verifiedBadge : styles.unverifiedBadge}
                            title={r.verified ? `Verified verbatim · ${(r as any).source} · checksum ${(r as any).checksum}` : 'Not yet verified against official source'}
                          >
                            {r.verified ? '✓ verbatim' : '⚠ unverified'}
                          </span>
                        )}
                        <p className={styles.recitalText}>{stripHtml(r.text)}</p>
                        {fnotes.length > 0 && (
                          <div className={styles.recitalFootnotes}>
                            {fnotes.map((fn: any) => (
                              <div key={fn.num} className={styles.recitalFnItem}>
                                <span className={styles.recitalFnNum}>({fn.num})</span>
                                <span className={styles.recitalFnText}>{fn.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}</>
            )}
            </div>
          )}
        </div>
      )}
      <div className={styles.articles}>
        {(filters.query.trim() || filters.hasMandates || filters.types.length > 0 || filters.statuses.length > 0) &&
          allSections.every(sec => sec.articles.every(a => !articleMatchesFilter(a, filters, INSTRUMENTS, articleSearchIndex))) && (
          <div className={styles.emptyHint}>No articles of the {act.shortName} match the current search or filters.</div>
        )}
        {allSections.map((section, si) => {
          const visibleArticles = section.articles.filter((a) =>
            articleMatchesFilter(a, filters, INSTRUMENTS, articleSearchIndex)
          );
          if (visibleArticles.length === 0) return null;

          return (
            <div key={si} className={styles.section}>
              <div className={styles.chapterTitle} data-tour={section.chapterTitle ? "section-group" : undefined}>{section.chapterTitle}</div>
              {section.sectionTitle && (
                <div className={styles.sectionTitle}>{section.sectionTitle}</div>
              )}
              {visibleArticles.map((article) => {
                const key = getArticleKey(article.num);
                const expanded = isExpanded(key);
                const hasMandates = article.rtsIts && article.rtsIts.length > 0;

                return (
                  <div
                    key={key}
                    className={`${styles.article} ${expanded ? styles.expanded : ''}`}
                    id={key}
                    data-article=""
                  >
                    <button
                      className={styles.articleHeader}
                      data-tour-xref={article.crossRefs && article.crossRefs.length > 0 ? '' : undefined}
                      data-tour-mandate={hasMandates ? '' : undefined}
                      onClick={() => toggle(key)}
                      aria-expanded={expanded}
                    >
                      <span className={styles.artNum}>{article.num}</span>
                      <span className={styles.artTitle}>{article.title}</span>
                      <div className={styles.artMeta}>
                        {article.crossRefs && article.crossRefs.length > 0 && (
                          <span className={styles.xrefCount} title={`${article.crossRefs.length} cross-reference${article.crossRefs.length > 1 ? 's' : ''}`}>{article.crossRefs.length}</span>
                        )}
                        {article.crossRefs && article.crossRefs.length > 0 && (
                          [...new Set(article.crossRefs.map(r => r.act))].sort((a,b) => ({amld:0,amlr:1,amlar:2,tfr:3}[a]??9)-({amld:0,amlr:1,amlar:2,tfr:3}[b]??9)).map(actId => (
                            <span
                              key={actId}
                              className={styles.xrefDot}
                              style={{ background: ACT_COLORS[actId] }}
                              title={actId.toUpperCase() + ' cross-reference'}
                            />
                          ))
                        )}
                        {hasMandates && (
                          <span className={styles.mandateDot} title={article.rtsIts!.map(id => INSTRUMENTS[id]?.type ?? id).join(', ')}>
                            {[...new Set(article.rtsIts!.map(id => INSTRUMENTS[id]?.type ?? '?'))].join('·')}
                          </span>
                        )}
                        {expanded && (
                          <a
                            className={styles.headerReadLink}
                            href={articleReaderHref ? articleReaderHref(article.num) : `#/article/${act.id}/${article.num}`}
                            target="_blank"
                            rel="noopener"
                            title="Open the full text in the article reader (new tab)"
                            onClick={e => e.stopPropagation()}
                          >Full text ↗</a>
                        )}
                        <span className={styles.chevron}>{expanded ? '▼' : '▶'}</span>
                      </div>
                    </button>
                    {/* Cross-reference badges — shown when expanded */}
                    {expanded && article.crossRefs && article.crossRefs.length > 0 && (
                      <div className={styles.crossRefs} data-tour="xref-badges">
                        {[...article.crossRefs].sort((a, b) => { const O: Record<string, number> = {amld:0,amlr:1,amlar:2,tfr:3}; const d = (O[a.act]??9)-(O[b.act]??9); return d!==0?d:parseInt(a.target.replace("art_",""))-parseInt(b.target.replace("art_","")); }).map((xr, xi) => (
                          <button
                            key={xi}
                            className={styles.crossRefBtn}
                            style={{
                              borderColor: ACT_COLORS[xr.act],
                              background: ACT_COLORS[xr.act] + '18',
                              color: ACT_COLORS[xr.act],
                            }}
                            onClick={e => { e.stopPropagation(); onCrossRefClick?.(xr.act, xr.target.replace('art_','')); }}
                            title={xr.type}
                          >
                            <span className={styles.crossRefAct}>{xr.act.toUpperCase()}</span>
                            <span className={styles.crossRefTarget}>Art. {xr.target.replace('art_','')}</span>
                            <span className={styles.crossRefType}>{xr.type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {expanded && (
                      <ArticleBody
                        article={article}
                        actId={act.id}
                        footnotes={act.footnotes}
                        onInstrumentClick={onInstrumentClick}
                        hideSources={articleHideSources}
                        readerHref={articleReaderHref ? articleReaderHref(article.num) : undefined}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {act.annexes && act.annexes.length > 0 && (
        <div className={styles.collapsibleSection}>
          <button
            className={`${styles.sectionToggle} ${styles.sectionToggleAnnexes}`}
            data-tour="annexes"
            onClick={() => setAnnexesOpen(o => !o)}
          >
            <span>📎 Annexes ({act.annexes.length})</span>
            <span>{annexesOpen ? '▼' : '▶'}</span>
          </button>
          {annexesOpen && (
            <div className={styles.annexList}>
              {act.annexes.map((a: Annex) => (
                <div key={a.id} className={styles.annexCard}>
                  <div className={styles.annexCardHeader}>
                    <span className={styles.annexTitle}>{a.title}</span>
                    {'verified' in a && (
                      <span
                        className={styles.annexIntegrityBadge}
                        title={a.verified ? `Verified verbatim — ${a.source}` : 'Unverified'}
                      >
                        {a.verified ? '✓' : '⚠'}
                      </span>
                    )}
                  </div>
                  {a.summary && (
                    <p className={styles.annexSummary}>{a.summary}</p>
                  )}
                  <button
                    className={styles.annexReaderBtn}
                    style={{ color: actColor, borderColor: actColor }}
                    onClick={() => openAnnexReader(a, act.id, act)}
                  >
                    📖 Read Annex ↗
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Strip HTML tags from CELLAR-extracted text.
// Uses >? to also catch incomplete tags cut off at extraction boundary
// (e.g. trailing `<div class="eli-subdivision"` without closing `>`).
function stripHtml(text: string): string {
  return text.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
}

// Builds the cited norms sidebar HTML for blob URL readers.
// Returns empty string if no norms to display.
function buildCitedNormsSidebar(normKeys: string[], color: string): string {
  const norms = normKeys
    .map(k => CITED_NORMS[k])
    .filter(Boolean);

  if (!norms.length) return '';

  const items = norms.map(n =>
    `<a class="cn-item" href="${n.url}" target="_blank" rel="noopener" title="${n.fullName}">
      <span class="cn-short">${n.shortName}</span>
      <span class="cn-code">${n.code}</span>
    </a>`
  ).join('');

  return `<aside class="cn-sidebar">
    <div class="cn-header" style="border-color:${color};color:${color}">Referenced legislation</div>
    <div class="cn-list">${items}</div>
  </aside>`;
}

// CSS to inject in blob HTML for the sidebar
const CITED_NORMS_CSS = `
.reader-layout{display:flex;gap:24px;align-items:flex-start}
.reader-main{flex:1;min-width:0}
.cn-sidebar{width:220px;flex-shrink:0;position:sticky;top:24px;max-height:calc(100vh - 48px);overflow-y:auto;border:1px solid #e5e7eb;border-radius:6px;background:#fafafa;font-size:11px}
.cn-header{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;padding:8px 10px;border-bottom:2px solid;background:#fff;border-radius:6px 6px 0 0;position:sticky;top:0}
.cn-list{padding:6px 4px}
.cn-item{display:block;padding:5px 8px;border-radius:4px;text-decoration:none;color:inherit;transition:background .15s;line-height:1.35}
.cn-item:hover{background:#f0f0f0}
.cn-short{display:block;font-weight:700;color:#111;font-size:11px}
.cn-code{display:block;color:#888;font-size:10px;margin-top:1px}
@media(max-width:680px){.reader-layout{flex-direction:column}.cn-sidebar{width:100%;position:static;max-height:none}}
@media print{.cn-sidebar{display:none}}
`;

export function openRecitalGroupReader(group: RecitalGroup, act: Act, actId: string) {
  const colors: Record<string, string> = { amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C' };
  const color = colors[actId] || '#333';
  const recitals = (act.recitals || []).filter((r: Recital) => group.recitalNums.includes(r.num));
  const footnotes = (act as any).footnotes || [];
  const recitalsHtml = recitals.map((r: Recital) => {
    const fnRefs = r.footnoteRefs || [];
    const fnHtml = fnRefs.length && footnotes.length
      ? '<div class="fn-list">' + fnRefs.map((n: number) => {
          const f = footnotes.find((x: any) => x.num === n);
          return f ? `<div class="fn-item"><span class="fn-num">(${n})</span><span>${f.text}</span></div>` : '';
        }).join('') + '</div>'
      : '';
    const isVerified = 'verified' in r && r.verified;
    const badgeHtml = isVerified
      ? `<span class="verbatim-badge verified" title="checksum ${(r as any).checksum}">✓ Full text taken verbatim from the <a href="${(r as any).source}" target="_blank" rel="noopener">official EUR-Lex source</a> ↗</span>`
      : `<span class="verbatim-badge unverified">⚠ Not yet verified against official source</span>`;
    return `<div class="recital"><div class="recital-header-row"><span class="recital-num">(${r.num})</span>${badgeHtml}</div><div class="recital-body"><p>${stripHtml(r.text)}</p>${fnHtml}</div></div>`;
  }).join('');
  const summaryHtml = group.superSummary
    ? `<div class="summary"><strong>Summary</strong><p>${group.superSummary}</p><div class="disclaimer">${group.summary_disclaimer || ''}</div></div>`
    : '';
  // Collect all citedNorms from recitals in this group
  const groupNormKeys = [...new Set(recitals.flatMap((r: Recital) => (r as any).citedNorms || []))];
  const sidebarHtml = buildCitedNormsSidebar(groupNormKeys, color);
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Recitals — ${group.title}</title>
<style>
${CITED_NORMS_CSS}
body{font-family:system-ui,sans-serif;max-width:960px;margin:40px auto;padding:0 24px;line-height:1.6;color:#1a1a1a}
h1{color:${color};border-bottom:3px solid ${color};font-size:20px;position:sticky;top:0;background:#fff;z-index:10;padding:14px 0 8px;box-shadow:0 6px 6px -6px rgba(0,0,0,.25)}
h2{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#555;margin-top:24px}
.summary{background:#f8f8f8;border-left:3px solid ${color};padding:12px 16px;margin:16px 0;border-radius:0 4px 4px 0}
.disclaimer{font-size:11px;color:#999;font-style:italic;margin-top:6px}
.recital{display:block;margin:0;padding:14px 0;border-bottom:1px solid #f0f0f0}.recital:last-child{border-bottom:none}
.recital-num{font-weight:700;color:${color};display:block;margin-bottom:4px}
.recital-header-row{display:flex;align-items:center;gap:10px;margin-bottom:6px;flex-wrap:wrap}
.recital-num{font-weight:700;color:${color};flex-shrink:0}
.verbatim-badge{display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:600;padding:3px 10px;border-radius:4px;flex:1}
.verbatim-badge.verified{background:#f0fdf4;color:#166534;border:1px solid #bbf7d0}
.verbatim-badge.verified a{color:#166534;font-weight:700}
.verbatim-badge.unverified{background:#fefce8;color:#854d0e;border:1px solid #fde68a}
.fn-list{margin-top:8px;padding-top:8px;border-top:1px solid #eee}
.fn-item{display:flex;gap:8px;font-size:12px;color:#555;margin:3px 0}
.fn-num{font-weight:700;flex-shrink:0}
footer{margin-top:40px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:12px}
${printLinkCss(color)}
</style></head><body>
<div class="reader-layout">
<div class="reader-main">
<h1>Recitals — ${group.title}</h1>
<div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap"><p style="color:#666;font-size:13px;margin:0">Recitals ${group.recitalNums[0]}–${group.recitalNums[group.recitalNums.length-1]} · ${group.recitalNums.length} recitals</p>${PRINT_LINK_HTML}</div>
${summaryHtml}
<h2>Full text</h2>
${recitalsHtml}
<footer>Always verify against the official EUR-Lex source.</footer>
</div>
${sidebarHtml}
</div>
</body></html>`;
  const blob = new Blob([html], { type: 'text/html' });
  window.open(URL.createObjectURL(blob));
}

// Renders a structured CpTable (headers + rows). Cells: string | string[] (multi-<p>)
// | { v, rowspan } (rowspan-merged; rows covered by the rowspan omit that column, and
// the browser places the remaining cells in the free columns). { section } = full-width band.
function renderCpTable(table: any): string {
  const esc = (s: any) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const ncol = table.headers.length;
  const cell = (c: any): string => {
    if (c && typeof c === 'object' && !Array.isArray(c)) {
      const rs = c.rowspan && c.rowspan > 1 ? ` rowspan="${c.rowspan}"` : '';
      const v = Array.isArray(c.v) ? c.v.map((p: string) => `<p>${esc(p)}</p>`).join('') : esc(c.v);
      return `<td${rs} class="cpt-span">${v}</td>`;
    }
    if (Array.isArray(c)) return `<td>${c.map((p: string) => `<p>${esc(p)}</p>`).join('')}</td>`;
    return `<td>${esc(c)}</td>`;
  };
  let html = '<table class="cp-table"><thead><tr>' +
    table.headers.map((h: string) => `<th>${esc(h)}</th>`).join('') + '</tr></thead><tbody>';
  for (const r of table.rows) {
    html += ('section' in r)
      ? `<tr class="cpt-band"><td colspan="${ncol}">${esc(r.section)}</td></tr>`
      : '<tr>' + r.cells.map(cell).join('') + '</tr>';
  }
  return html + '</tbody></table>';
}

export function openAnnexReader(annex: Annex, actId: string, act: Act) {
  const colors: Record<string, string> = { amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C' };
  const actLabels: Record<string, string> = { amld: 'AMLD 2024/1640', amlr: 'AMLR 2024/1624', amlar: 'AMLAR 2024/1620', tfr: 'TFR 2023/1113' };
  const color = colors[actId] || '#333';
  const actLabel = actLabels[actId] || actId.toUpperCase();
  const footnotes = (act as any).footnotes || [];

  // Structured annex renderer — preserves OJ formatting
  // stripHtml collapses \n via /\s+/g — use newline-safe version for annexes
  const cleanText = annex.text.replace(/<[^>]*>?/gm, ' ');
  const lines = cleanText.split(/\n/).map((l: string) => l.replace(/[ \t]+/g, ' ').trim()).filter(Boolean);

  function renderAnnexLines(ls: string[]): string {
    // ── Correlation table detection ───────────────────────────────────────
    const isTableRow = (l: string) => /^Article\s+[\d(]|^—$/.test(l);
    const tableStart = ls.findIndex(isTableRow);
    const hasTable = tableStart >= 0 && ls.slice(tableStart, tableStart + 6).filter(isTableRow).length >= 3;

    if (hasTable) {
      const preLines = ls.slice(0, tableStart);
      const tableLines = ls.slice(tableStart);
      // Detect column count from header lines preceding table
      const headerCandidates = preLines.filter(l => /^(Directive|Regulation|This\s)/i.test(l));
      const colCount = headerCandidates.length >= 2 ? headerCandidates.length : 3;
      const cols = colCount === 2
        ? headerCandidates.slice(0, 2)
        : headerCandidates.length >= 3 ? headerCandidates.slice(0, 3)
        : ['Column 1', 'Column 2', 'Column 3'];
      const preHtml = preLines.map(l => `<p>${l}</p>`).join('');
      let tableHtml = '<table class="corr-table"><thead><tr>' +
        cols.map(c => `<th>${c}</th>`).join('') + '</tr></thead><tbody>';
      for (let i = 0; i < tableLines.length; i += colCount) {
        const row = tableLines.slice(i, i + colCount);
        if (row.length === colCount) {
          tableHtml += '<tr>' + row.map(c => `<td>${c}</td>`).join('') + '</tr>';
        }
      }
      tableHtml += '</tbody></table>';
      return preHtml + tableHtml;
    }

    // ── Structured list renderer ─────────────────────────────────────────
    let html = '';
    let inLettered = false;
    let inRoman = false;
    let inNumbered = false;

    for (let idx = 0; idx < ls.length; idx++) {
      const line = ls[idx];
      const isRoman    = /^\((i{1,3}|iv|vi{0,3}|ix|xi{0,3}|xiv|xvi{0,3}|xix|xx)\)\s/.test(line); // precise roman i–xx
      const isLettered = !isRoman && /^\([a-z]\)\s/.test(line); // (a)(b)(c) excl. roman
      const isParenNum = /^\(\d+\)$/.test(line);              // (1) alone on a line = section header
      const isDotNum   = /^\d+\.(\s|$)/.test(line);               // 1. 2.
      const isDotRoman = /^[IVX]+\.\s?/.test(line);            // I. II.

      if (isParenNum) {
        // Section number alone: combine with next line as section title
        if (inRoman)    { html += '</ul>'; inRoman = false; }
        if (inLettered) { html += '</ul>'; inLettered = false; }
        if (inNumbered) { html += '</ul>'; inNumbered = false; }
        const nextLine = ls[idx + 1] || '';
        const isNextTitle = nextLine && !(/^\(/.test(nextLine)) && !isDotNum;
        if (isNextTitle) {
          html += `<div class="${nextLine.trimEnd().endsWith(':') ? 'anx-section-hdr' : 'anx-section'}"><span class="anx-section-num">${line}</span> <span class="anx-section-title">${nextLine}</span></div>`;
          idx++; // skip next line — already consumed
        } else {
          html += `<p class="anx-section-num-solo">${line}</p>`;
        }
      } else if (isDotRoman || isDotNum) {
        // I. / II. or 1. / 2. — section headers for AMLAR-style annexes
        if (inRoman)    { html += '</ul>'; inRoman = false; }
        if (inLettered) { html += '</ul>'; inLettered = false; }
        if (inNumbered) { html += '</ul>'; inNumbered = false; }
        const nextL = ls[idx + 1] || '';
        const nextIsPlain = nextL && !/^[\(\d]/.test(nextL) && !/^[IVX]+\./.test(nextL);
        if (nextIsPlain) {
          html += `<p class="${nextL.trimEnd().endsWith(':') ? 'anx-dot-header' : 'anx-dot-item'}"><span class="anx-dot-num">${line}</span> ${nextL}</p>`;
          idx++;
        } else {
          html += `<p class="anx-dot-header">${line}</p>`;
        }
      } else if (isLettered) {
        if (inRoman)    { html += '</ul>'; inRoman = false; }
        if (!inLettered) { html += '<ul class="anx-list-a">'; inLettered = true; }
        const isHdr = line.trimEnd().endsWith(':');
          html += `<li class="${isHdr ? 'anx-item-a-hdr' : 'anx-item-a'}">${line}</li>`;
      } else if (isRoman) {
        if (!inRoman) { html += '<ul class="anx-list-i">'; inRoman = true; }
        html += `<li class="anx-item-i">${line}</li>`;
      } else {
        if (inRoman)    { html += '</ul>'; inRoman = false; }
        if (inLettered) { html += '</ul>'; inLettered = false; }
        if (inNumbered) { html += '</ul>'; inNumbered = false; }
        html += `<p>${line}</p>`;
      }
    }
    if (inRoman)    html += '</ul>';
    if (inLettered) html += '</ul>';
    if (inNumbered) html += '</ul>';
    return html;
  }

  const textHtml = renderAnnexLines(lines);
  const tableHtml = (annex as any).table ? renderCpTable((annex as any).table) : '';

  // Footnotes referenced by this annex
  const fnRefs = annex.footnoteRefs || [];
  const fnHtml = fnRefs.length && footnotes.length
    ? '<div class="fn-section"><h2>Footnotes</h2><ol class="fn-list">' +
      fnRefs.map((n: number) => {
        const f = footnotes.find((x: any) => x.num === n);
        return f ? `<li class="fn-item"><span class="fn-num">(${n})</span><span>${f.text}</span></li>` : '';
      }).join('') + '</ol></div>'
    : '';

  // Cross-references
  const crossRefLabels: Record<string, string> = {
    'implements': 'Implements', 'implemented-by': 'Implemented by',
    'see-also': 'See also', 'derogates': 'Derogates', 'derogated-by': 'Derogated by',
    'defined-in': 'Defined in', 'supersedes': 'Supersedes',
  };
  const crossRefs = annex.crossRefs || [];
  const crossRefsHtml = crossRefs.length
    ? '<div class="xref-section"><h2>Cross-references</h2><ul class="xref-list">' +
      crossRefs.map((x: any) => {
        const label = crossRefLabels[x.type] || x.type;
        return `<li class="xref-item"><span class="xref-type">${label}</span><span class="xref-target">${x.act.toUpperCase()} ${x.target.replace('art_', 'Art. ').replace('rct_', 'Recital ')}</span></li>`;
      }).join('') + '</ul></div>'
    : '';

  // Integrity banner
  const vstate: any = ('verified' in annex && (annex as any).verified) || false;
  const isDraft = vstate === 'amla_draft';
  const isVerified = vstate === true;
  const integrityHtml = isDraft
    ? `<div class="integrity-banner draft">◆ AMLA draft — verbatim from the official AMLA document${(annex as any).checksum ? ` · checksum ${(annex as any).checksum}` : ''}. Not yet OJ-published; verify against the final adopted text.</div>`
    : `<div class="integrity-banner ${isVerified ? 'verified' : 'unverified'}">
    ${isVerified
      ? `✓ Verbatim text — fetched from <a href="${(annex as any).source}" target="_blank" rel="noopener">EUR-Lex official source</a> · checksum ${(annex as any).checksum}`
      : '⚠ Text not yet verified against official source'}
  </div>`;

  // Summary
  const summaryHtml = annex.summary
    ? `<div class="summary"><strong>Summary</strong><p>${annex.summary}</p><div class="disclaimer">${annex.summary_disclaimer || ''}</div></div>`
    : '';

  // Source link
  const annexSource = (annex as any).source;
  const isAmlaSource = !!annexSource && /amla\.europa\.eu/.test(annexSource);
  const sourceUrl = annexSource || `https://eur-lex.europa.eu/eli/${actId === 'amld' ? 'dir' : 'reg'}/${actId === 'amld' ? '2024/1640' : actId === 'amlr' ? '2024/1624' : actId === 'amlar' ? '2024/1620' : '2023/1113'}/oj/eng`;
  const sourceLabel = (isDraft || isAmlaSource) ? 'AMLA — Official document ↗' : 'Full act on EUR-Lex ↗';

  const annexNormKeys = (annex as any).citedNorms || [];
  const annexSidebarHtml = buildCitedNormsSidebar(annexNormKeys, color);
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>${annex.title}</title>
<style>
${CITED_NORMS_CSS}
body{font-family:system-ui,sans-serif;max-width:960px;margin:40px auto;padding:0 24px;line-height:1.7;color:#1a1a1a}
h1{color:${color};border-bottom:3px solid ${color};padding-bottom:8px;font-size:20px;margin-bottom:4px}
h2{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#555;margin-top:28px;margin-bottom:8px}
.breadcrumb{font-size:12px;color:#888;margin-bottom:20px}
.integrity-banner{font-size:11px;padding:8px 12px;border-radius:4px;margin:12px 0}
.integrity-banner.verified{background:#f0fdf4;color:#166534;border:1px solid #bbf7d0}
.integrity-banner.unverified{background:#fefce8;color:#854d0e;border:1px solid #fde68a}
.integrity-banner a{color:inherit}
.summary{background:#f8f8f8;border-left:3px solid ${color};padding:12px 16px;margin:16px 0;border-radius:0 4px 4px 0}
.disclaimer{font-size:11px;color:#999;font-style:italic;margin-top:6px}
.sources{display:flex;gap:8px;margin:16px 0;flex-wrap:wrap}
.source-btn{display:inline-flex;align-items:center;gap:4px;padding:6px 12px;border-radius:4px;font-size:12px;font-weight:600;text-decoration:none;background:${color};color:#fff}
.source-btn:hover{opacity:.85}
.verbatim-text{background:#fafafa;border:1px solid #e5e7eb;border-radius:6px;padding:20px 24px;margin:16px 0}
.verbatim-text p{margin:0 0 12px 0}.verbatim-text p:last-child{margin-bottom:0}
.anx-list-a{margin:4px 0 4px 16px;padding:0;list-style:none}
.anx-item-a{padding:3px 0;font-weight:400;color:#1a1a1a}\n.anx-item-a-hdr{padding:4px 0;font-weight:600;color:#1a1a1a;margin-top:6px}
.anx-list-i{margin:2px 0 6px 32px;padding:0;list-style:none}
.anx-item-i{padding:2px 0;font-weight:400;color:#374151;border-left:2px solid #e5e7eb;padding-left:10px;margin:2px 0}
.corr-table{width:100%;border-collapse:collapse;font-size:12px;margin:12px 0}
.corr-table th{background:#f1f5f9;padding:6px 10px;text-align:left;font-weight:600;border:1px solid #e2e8f0}
.corr-table td{padding:5px 10px;border:1px solid #e2e8f0;vertical-align:top}
.corr-table tr:nth-child(even){background:#f8fafc}
.integrity-banner.draft{background:#fffbeb;color:#92400e;border:1px solid #fde68a}
.cp-table-wrap{overflow-x:auto;margin:8px 0}
.cp-table{border-collapse:collapse;width:100%;font-size:11px}
.cp-table th{background:${color};color:#fff;padding:5px 7px;text-align:left;font-weight:600;border:1px solid #fff;white-space:nowrap}
.cp-table td{border:1px solid #d1d5db;padding:4px 7px;vertical-align:top}
.cp-table td.cpt-span{background:${color}14;font-weight:600}
.cp-table tbody tr:nth-child(even) td:not(.cpt-span){background:#fafafa}
.cp-table p{margin:0 0 4px}.cp-table p:last-child{margin:0}
.cpt-band td{background:${color}26;font-weight:700}
.anx-section{font-weight:400;margin:10px 0 2px;color:#374151}\n.anx-section-hdr{font-weight:600;margin:10px 0 2px;color:#1a1a1a}
.anx-section-num{color:#6b7280}
.anx-section-title{color:#1a1a1a}
.anx-section-num-solo{font-weight:400;color:#6b7280;margin:6px 0 2px}
.anx-dot-header{font-weight:700;margin:14px 0 4px;color:#1a1a1a}\n.anx-dot-item{font-weight:400;margin:6px 0 2px;color:#1a1a1a}\n.anx-dot-num{color:#6b7280;margin-right:2px}
.fn-section,.xref-section{margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb}
.fn-list{list-style:none;padding:0;margin:0}
.fn-item{display:flex;gap:8px;font-size:12px;color:#555;margin:4px 0}
.fn-num{font-weight:700;flex-shrink:0;color:${color}}
.xref-list{list-style:none;padding:0;margin:0}
.xref-item{display:flex;gap:8px;font-size:12px;padding:4px 0;border-bottom:1px solid #f0f0f0}
.xref-type{font-weight:600;color:#555;min-width:110px;flex-shrink:0}
.xref-target{color:#1a1a1a}
footer{margin-top:40px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:12px;display:flex;justify-content:space-between}
@media print{.source-btn{display:none}body{margin:0}footer{position:fixed;bottom:0;width:100%}}
${printLinkCss(color)}
</style></head><body>
<div class="reader-layout">
<div class="reader-main">
<div class="breadcrumb">${actLabel} · Annexes</div>
<h1>${annex.title}</h1>
${integrityHtml}
${summaryHtml}
<div class="sources">
  <a class="source-btn" href="${sourceUrl}" target="_blank" rel="noopener">📖 ${sourceLabel}</a>
  ${PRINT_LINK_HTML}
</div>
${textHtml.trim() ? `<h2>Full text</h2>\n<div class="verbatim-text">${textHtml}</div>` : ''}
${tableHtml ? `<h2>Data points matrix</h2>\n<div class="cp-table-wrap">${tableHtml}</div>` : ''}
${fnHtml}
${crossRefsHtml}
</div>
${annexSidebarHtml}
</div>
<footer>
  <span>AML Navigator · ${annex.title}</span>
  <span>Always verify against the official EUR-Lex source.</span>
</footer>
</body></html>`;

  const blob = new Blob([html], { type: 'text/html' });
  window.open(URL.createObjectURL(blob));
}
